/**
 * Personal pace model: ETAs based on how THIS driver actually drives, not on
 * how fast the road theoretically allows.
 *
 * Mapbox's duration assumes a generic vehicle at roughly the posted limit. A
 * loaded Hino does not do 120 on the motorway, and a driver who cruises at 90
 * gets an ETA that is optimistic on every single leg - the error compounds over
 * a 300 km run. So the driver sets their own top speed for three road tiers and
 * we re-derive the journey time from those.
 *
 * The settings speeds are a CEILING, not an average
 * -------------------------------------------------
 * "the speeds we put in should be the max we ever drive on the certain kind of
 * road." So a tier speed is the fastest this driver will ever go on that class
 * of road - not the speed they average over a leg of it, which is always lower
 * once junctions, towns, queues and roadworks are in.
 *
 * This distinction was the whole bug. The model used to divide each step's
 * distance by the tier speed, i.e. it spent a MAXIMUM as though it were a
 * sustained mean. That made every modelled duration land below Mapbox's
 * traffic-aware figure, and since the two are combined with `Math.max` the
 * driver's numbers were then thrown away on essentially every journey. Measured
 * against the live API on Bang Saen -> Hua Hin and Suvarnabhumi -> Hua Hin, ten
 * of eleven candidate routes came back Mapbox-bound: the app was showing the
 * generic-vehicle estimate while Settings claimed journey times came from these
 * speeds. Bang Saen -> Hua Hin read 3h29 against a real 4h-ish drive, only 32
 * minutes more than the same trip from Suvarnabhumi, which is 55 km shorter.
 *
 * So the speeds are now applied the way a ceiling has to be applied: per
 * geometry segment, take the SLOWEST of
 *   - how fast live traffic says that segment is actually moving,
 *   - the posted limit there, and
 *   - the fastest this driver ever drives that tier.
 * A jammed segment keeps its jam; a free-flowing motorway segment that Mapbox
 * times at 120 gets driven at the driver's 110. The result can only ever be
 * slower than the router's, never faster, which is the property we want.
 *
 * Red lights are NOT added on top
 * -------------------------------
 * `signalSeconds` is still counted and still shown on the route card, but it is
 * explanatory only - the same rule nav/routeStats.ts already follows. The live
 * segment speeds we are capping already have stopped traffic in them, so adding
 * a per-light cost on top counts the same queue twice. It used to be added, and
 * on a 28-signal route that was nearly an hour of double-counting.
 *
 * How a step's tier is decided
 * ----------------------------
 * Three tiers, and the line between the first two is NOT lane count:
 *
 *   motorway - the 120 km/h motorways. In Thailand that is มอเตอร์เวย์ 7 and 9
 *              and the tolled expressways: grade-separated, no at-grade
 *              junctions, no traffic lights.
 *   dual     - two or more lanes each way but still an ordinary road: the
 *              trunk network (2, 24, 304) and the provincial primaries. Wide
 *              and fast, but with junctions, U-turn gaps and lights.
 *   single   - one lane each way, sois, service roads.
 *
 * The distinction matters because a trunk road is not a motorway. It used to be
 * classed as one, which meant the 24 and the 304 were being timed at the
 * motorway cruising speed on a road that has traffic lights on it - optimistic
 * on exactly the roads most of these journeys are made of.
 *
 * Lane counts cannot decide this. `intersections[].lanes` is TURN-lane data at
 * junctions, so a one-lane soi with a left/straight/right arrangement reports
 * three lanes; and a three-lane trunk road is still not a motorway, because
 * motorway status is about grade separation, not width. Road class is the
 * reliable signal, so class leads and lane counts only ever refine downwards.
 *
 * Traffic still wins when it is worse
 * -----------------------------------
 * The whole-route figure is still floored by the router's own duration, so we
 * can never claim to beat a jam even if a tier ceiling somehow came out high.
 */

import type { Route, RouteStep } from '../core/types';

export type RoadTier = 'motorway' | 'dual' | 'single';

/**
 * TOP speeds in km/h, per road tier - the fastest this driver ever goes on that
 * class of road, not the speed they average across it. See the header.
 */
export interface PaceSpeeds {
  /** Grade-separated 120 km/h motorways and tolled expressways. */
  motorway: number;
  /** Dual carriageway: 2+ lanes each way, but with junctions - trunk, primary. */
  dual: number;
  /** Single-lane roads, sois, service roads. */
  single: number;
}

export const DEFAULT_SPEEDS: PaceSpeeds = {
  // A 120 limit, and what a loaded truck will actually wind up to on it.
  motorway: 110,
  dual: 85,
  single: 55,
};

const CLASS_TIER: Record<string, RoadTier> = {
  // Only genuine motorways. Trunk roads are dual carriageways: wide and fast,
  // but with at-grade junctions and lights, which is a different drive.
  motorway: 'motorway',
  motorway_link: 'motorway',
  trunk: 'dual',
  trunk_link: 'dual',
  primary: 'dual',
  primary_link: 'dual',
  secondary: 'dual',
  secondary_link: 'dual',
  tertiary: 'single',
  tertiary_link: 'single',
  street: 'single',
  street_limited: 'single',
  service: 'single',
  track: 'single',
  path: 'single',
  ferry: 'single',
};

/**
 * Which tier a step belongs to. Road class leads; a consistent lane count
 * across the step's junctions can promote or demote it by one tier.
 */
export function tierForStep(step: RouteStep): RoadTier {
  const classes: string[] = [];
  const laneCounts: number[] = [];

  for (const x of step.intersections ?? []) {
    const cls = x.mapbox_streets_v8?.class;
    if (cls) classes.push(cls);
    if (x.lanes?.length) laneCounts.push(x.lanes.length);
  }

  // Most common class along the step.
  let tier: RoadTier = 'single';
  if (classes.length) {
    const tally = new Map<string, number>();
    for (const c of classes) tally.set(c, (tally.get(c) ?? 0) + 1);
    let bestClass = '';
    let best = 0;
    for (const [c, n] of tally) {
      if (n > best) {
        best = n;
        bestClass = c;
      }
    }
    tier = CLASS_TIER[bestClass] ?? 'single';
  }

  /*
   * Lane evidence refines DOWNWARDS only.
   *
   * A wide trunk road is not promoted to motorway however many lanes it has -
   * motorway means grade-separated, and no lane count can establish that. But
   * a `primary` or `secondary` that consistently shows a single lane really is
   * a single carriageway whatever the class says, and timing it at dual-
   * carriageway speed would be wrong. Only when several junctions agree.
   */
  if (laneCounts.length >= 2) {
    const sorted = [...laneCounts].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    if (median <= 1 && tier === 'dual') tier = 'single';
  }

  return tier;
}

/**
 * Seconds lost at an average signalised junction. Thai cycles are long.
 *
 * Used to DESCRIBE a route ("18 min at lights"), not to lengthen its ETA - the
 * capped live speeds already carry the queue. See the header.
 */
export const DEFAULT_SIGNAL_SECONDS = 120;

export interface PaceEstimate {
  /** Seconds, all-in: capped rolling time, floored by live traffic. */
  duration: number;
  /** Seconds actually rolling, at the capped speeds. */
  rollingSeconds: number;
  /**
   * Seconds this route plausibly spends sat at red lights. EXPLANATORY ONLY -
   * it is not part of `duration`; the capped segment speeds already contain the
   * queue. Shown on the route card so two similar ETAs can be told apart.
   */
  signalSeconds: number;
  /** Signalised junctions counted on this route. */
  signals: number;
  /** Metres driven in each tier, for explaining the estimate. */
  distanceByTier: Record<RoadTier, number>;
  /** Metres where the driver's own ceiling, not traffic, set the speed. */
  cappedMetres: number;
  /** How much slower (positive) or faster (negative) than Mapbox's estimate. */
  deltaVsRouter: number;
  /** True when live traffic was worse than the model and set the figure. */
  trafficBound: boolean;
}

/** km/h -> m/s, with a floor so a bad value can never divide by ~zero. */
function mps(kmh: number): number {
  return Math.max(5, kmh) / 3.6;
}

/*
 * Note on the posted limit: it is deliberately NOT a third cap here.
 *
 * It was, briefly, and it made the ETA pessimistic for a reason that has
 * nothing to do with this driver - on Thai trunk roads the traffic genuinely
 * flows above the sign, so capping at the sign invents delay that nobody
 * experiences. The posted limit is something the driver weighs when choosing
 * their own number for a tier, which is where it belongs. In here there are
 * exactly two honest constraints: how fast the road is moving, and how fast
 * this driver goes.
 */

/**
 * Re-time a route with the driver's top speeds applied as a per-segment
 * ceiling.
 *
 * Walks the geometry segment by segment rather than step by step, because
 * within one 40 km motorway step the traffic varies enormously and a step-level
 * average hides it: capping a step whose mean is already below the ceiling does
 * nothing, while capping the free-flowing half of it is exactly the correction
 * we want. Mapbox's `annotation` arrays give distance, live speed and posted
 * limit at that resolution.
 *
 * Falls back to step granularity when a response carries no annotation - the
 * same ceiling rule, just coarser - so an ETA is always produced.
 */
export function estimateWithPace(
  route: Route,
  speeds: PaceSpeeds,
  signalSeconds = DEFAULT_SIGNAL_SECONDS,
): PaceEstimate {
  const distanceByTier: Record<RoadTier, number> = { motorway: 0, dual: 0, single: 0 };
  let rollingSeconds = 0;
  let cappedMetres = 0;
  let signals = 0;

  for (const leg of route.legs) {
    /*
     * Tier per geometry segment. Steps are contiguous and share their end
     * coordinates, so each contributes (its coordinate count - 1) segments and
     * laying them end to end lines up with the annotation arrays exactly -
     * except for the zero-length steps, which must be skipped. Every leg ends
     * with an `arrive` step whose geometry is the same point twice: it looks
     * like one more segment but the annotation has no entry for it, and
     * counting it shifted the whole leg by one and failed the length check
     * below, quietly demoting every single route to the coarse fallback.
     */
    const segTiers: RoadTier[] = [];
    for (const step of leg.steps) {
      const tier = tierForStep(step);
      distanceByTier[tier] += step.distance;
      for (const x of step.intersections ?? []) {
        if (x.traffic_signal) signals++;
      }
      if (!(step.distance > 0)) continue;
      const n = Math.max(0, (step.geometry?.coordinates?.length ?? 1) - 1);
      for (let i = 0; i < n; i++) segTiers.push(tier);
    }

    const ann = leg.annotation;
    const dists = ann?.distance;

    // The alignment above is only safe if the two agree on how many segments
    // this leg has. If they don't, the mapping is untrustworthy and quietly
    // timing the wrong tiers is worse than being coarse - drop to steps.
    if (dists?.length && dists.length === segTiers.length) {
      for (let i = 0; i < dists.length; i++) {
        const d = dists[i];
        if (!(d > 0)) continue;
        const ceiling = mps(speeds[segTiers[i]] || DEFAULT_SPEEDS[segTiers[i]]);

        // Live speed, or derive it from this segment's own duration.
        const live = ann?.speed?.[i] ?? (ann?.duration?.[i] ? d / ann.duration[i] : null);
        const flowing = live != null && live > 0 ? live : ceiling;

        const v = Math.max(1, Math.min(ceiling, flowing));
        if (ceiling < flowing) cappedMetres += d; // the driver, not the traffic
        rollingSeconds += d / v;
      }
    } else {
      for (const step of leg.steps) {
        if (!(step.distance > 0)) continue;
        const ceiling = mps(speeds[tierForStep(step)] || DEFAULT_SPEEDS[tierForStep(step)]);
        const live = step.duration > 0 ? step.distance / step.duration : null;
        const v = Math.max(1, live != null && live > 0 ? Math.min(ceiling, live) : ceiling);
        if (live != null && ceiling < live) cappedMetres += step.distance;
        rollingSeconds += step.distance / v;
      }
    }
  }

  // Explanatory only - deliberately NOT part of `duration`. See the header.
  const signalTotal = signals * Math.max(0, signalSeconds);

  // Steps can be missing on some responses; never return a nonsense zero.
  const base = rollingSeconds > 0 ? rollingSeconds : route.duration;
  const trafficBound = route.duration > base;
  const duration = Math.max(base, route.duration);

  return {
    duration,
    rollingSeconds,
    signalSeconds: signalTotal,
    signals,
    distanceByTier,
    cappedMetres,
    deltaVsRouter: duration - route.duration,
    trafficBound,
  };
}

/**
 * Attach the pace estimate to each route and re-rank fastest-first.
 *
 * The re-rank is the point: capping every segment at what this driver actually
 * does costs a motorway route far more than a town route, because the motorway
 * is where the router assumed 120 and the truck does 90. Two options Mapbox
 * calls a minute apart can separate by fifteen once that is applied, and its
 * own ordering will never show it.
 */
export function applyPace(
  routes: Route[],
  speeds: PaceSpeeds,
  signalSeconds = DEFAULT_SIGNAL_SECONDS,
): Route[] {
  for (const r of routes) {
    const est = estimateWithPace(r, speeds, signalSeconds);
    r._paceDuration = est.duration;
    r._paceSignalSeconds = est.signalSeconds;
  }
  return [...routes].sort((a, b) => paceDuration(a) - paceDuration(b));
}

/** The duration to show and rank by: personal pace when we have it. */
export function paceDuration(route: Route): number {
  return route._paceDuration ?? route.duration;
}
